import type { SessionRefresher } from '@/shared/api/session-refresher';
import type { SessionStore } from '@/shared/api/session-store';

/** Antecedência da renovação em relação à expiração do access token. */
export const RENEWAL_LEAD_TIME_MS = 60_000;

/** Menor espera entre agendamentos (evita laço se o servidor devolver token já vencido). */
const MIN_DELAY_MS = 1_000;

/** Dependências da renovação silenciosa. */
export interface SilentRenewalOptions {
  store: SessionStore;
  refresher: SessionRefresher;
}

/**
 * Renova o access token em silêncio um minuto antes de ele expirar, reagendando a cada token
 * novo. Ao voltar para a aba (timers em segundo plano atrasam), renova na hora se o token já
 * estiver vencendo. Usa o refresh single-flight compartilhado. Devolve a função que para tudo.
 */
export function startSilentRenewal({ store, refresher }: SilentRenewalOptions): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let scheduledFor: string | null = null;

  const renewAt = (expiresAt: string): number => Date.parse(expiresAt) - RENEWAL_LEAD_TIME_MS;

  const cancel = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    scheduledFor = null;
  };

  const renew = (): void => {
    cancel();
    void refresher.refresh();
  };

  const schedule = (): void => {
    const { status, expiresAt } = store.getState();
    if (status !== 'authenticated' || expiresAt === null) {
      cancel();
      return;
    }
    if (expiresAt === scheduledFor) return;
    cancel();
    scheduledFor = expiresAt;
    timer = setTimeout(renew, Math.max(renewAt(expiresAt) - Date.now(), MIN_DELAY_MS));
  };

  const renewIfDueOnReturn = (): void => {
    if (document.visibilityState !== 'visible') return;
    const { status, expiresAt } = store.getState();
    if (status === 'authenticated' && expiresAt !== null && Date.now() >= renewAt(expiresAt)) {
      renew();
    }
  };

  schedule();
  const unsubscribe = store.subscribe(schedule);
  document.addEventListener('visibilitychange', renewIfDueOnReturn);

  return () => {
    unsubscribe();
    document.removeEventListener('visibilitychange', renewIfDueOnReturn);
    cancel();
  };
}
